import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Stack,
  Button,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { CourtPayloadType, CourtTypes } from "../../types/generalTypes";
import { colors } from "../../constants/Colors";
import { updateCourt } from "../../api/CourtAPI";
import { MODALITIES_MAP } from "../../constants/Global";

interface DetailsCourtModalProps {
  open: boolean;
  closeModal: () => void;
  court: CourtTypes;
  refetchCourts: () => Promise<void>;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", md: "600px" },
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
  maxHeight: "90vh",
};

const DetailsCourtModal = ({
  court,
  open,
  closeModal,
  refetchCourts,
}: DetailsCourtModalProps) => {
  const [name, setName] = useState(court.name);
  const [description, setDescription] = useState(court.description);

  const [selectedModalities, setSelectedModalities] = useState<number[]>(
    court.modality
      ? court.modality.split(",").map((id) => parseInt(id.trim()))
      : [],
  );

  const [loading, setLoading] = useState(false);

  const toggleModality = (id: number) => {
    setSelectedModalities((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const onEditCourt = async () => {
    setLoading(true);
    try {
      const data: CourtPayloadType = {
        courtId: court.courtId,
        company_id: court.companyId,
        description: description,
        name: name,
        modality: selectedModalities.join(","),
      };

      await updateCourt(data);
      await refetchCourts();
      closeModal();
    } finally {
      setLoading(false);
    }
  };

  const isDisabled =
    name === "" ||
    description === "" ||
    selectedModalities.length === 0 ||
    loading;

  return (
    <Modal open={open} onClose={closeModal}>
      <Box sx={style}>
        <Stack spacing={2}>
          <Typography sx={{ fontWeight: 600 }} fontSize="18px" color="#22303E">
            Editar Quadra
          </Typography>

          <FormControl>
            <FormLabel>Nome</FormLabel>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              disabled={loading}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Descrição</FormLabel>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              disabled={loading}
            />
          </FormControl>

          <FormControl>
            <FormLabel sx={{ mb: 1 }}>Modalidades</FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {Object.entries(MODALITIES_MAP).map(([idStr, value]) => {
                const id = parseInt(idStr);
                const isSelected = selectedModalities.includes(id);
                return (
                  <Box
                    key={id}
                    onClick={() => !loading && toggleModality(id)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100px",
                      height: "100px",
                      border: `2px solid ${isSelected ? colors.blue : "#E0E0E0"}`,
                      borderRadius: "8px",
                      cursor: loading ? "default" : "pointer",
                      transition: "0.2s",
                      "&:hover": { borderColor: colors.blue },
                    }}
                  >
                    <img
                      src={value.icon}
                      alt={value.label}
                      style={{
                        width: "30px",
                        height: "30px",
                        filter: isSelected ? "none" : "grayscale(100%)",
                        opacity: isSelected ? 1 : 0.5,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, fontWeight: isSelected ? 600 : 400 }}
                    >
                      {value.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, pt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              sx={{ background: "#ffffff", color: "#3e3e3e" }}
              onClick={closeModal}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={isDisabled}
              sx={{ background: colors.blue }}
              onClick={onEditCourt}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Salvar"
              )}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default DetailsCourtModal;
